// smööts AI chat (David 29.9.2026, Huel-style): the inline "Ask smööts AI" widget opens a modal chat.
// Answers stream from the smoots-ai Vercel service; [[product:handle]] tags in an answer become product cards.
import { emit, routes, lockScroll } from './sm-core.js';

const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function md(text) {
  const lines = esc(text).split('\n');
  let html = '';
  let list = false;
  for (const raw of lines) {
    let l = raw.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g, '<a href="$2">$1</a>');
    if (/^\s*[-*•] /.test(l)) {
      if (!list) { html += '<ul>'; list = true; }
      html += `<li>${l.replace(/^\s*[-*•] /, '')}</li>`;
      continue;
    }
    if (list) { html += '</ul>'; list = false; }
    if (l.trim()) html += `<p>${l}</p>`;
  }
  if (list) html += '</ul>';
  return html;
}
const money = (cents, locale) => new Intl.NumberFormat(locale || 'hr-HR', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);

class SmAiChat {
  constructor(cfg) {
    this.cfg = cfg;
    this.history = [];
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'sm-ai-chat';
    this.dialog.setAttribute('aria-label', cfg.title);
    this.dialog.innerHTML = `
      <div class="sm-ai-chat__head">
        <button type="button" class="sm-ai-chat__reset" data-reset>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5"/></svg>
          <span>${esc(cfg.reset)}</span>
        </button>
        <b class="sm-ai-chat__title">${esc(cfg.title)}</b>
        <button type="button" class="sm-ai-chat__close" data-close aria-label="${esc(cfg.close)}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
        </button>
      </div>
      <div class="sm-ai-chat__body" data-body aria-live="polite"></div>
      <form class="sm-ai-chat__foot" data-form>
        <div class="sm-ai-chat__field">
          <label class="sm-visually-hidden" for="sm-ai-chat-input">${esc(cfg.placeholder)}</label>
          <input id="sm-ai-chat-input" type="text" autocomplete="off" maxlength="600" placeholder="${esc(cfg.placeholder)}" data-input>
          <button type="submit" class="sm-ai-chat__send" aria-label="${esc(cfg.send)}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          </button>
        </div>
        <p class="sm-ai-chat__legal">${cfg.legal}</p>
      </form>`;
    document.body.append(this.dialog);
    this.body = this.dialog.querySelector('[data-body]');
    this.input = this.dialog.querySelector('[data-input]');
    this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
    // page behind the chat does not scroll while it is open (Esc, X, backdrop and Buy now all fire close)
    this.dialog.addEventListener('close', () => lockScroll(false));
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.querySelector('[data-reset]').addEventListener('click', () => { this.history = []; this.body.innerHTML = ''; this.input.focus(); });
    this.dialog.querySelector('[data-form]').addEventListener('submit', (e) => { e.preventDefault(); this.ask(this.input.value); });
  }

  open() {
    if (!this.dialog.open) { this.dialog.showModal(); lockScroll(true); }
  }

  bubble(role, html) {
    const el = document.createElement('div');
    el.className = `sm-ai-chat__msg sm-ai-chat__msg--${role}`;
    el.innerHTML = html;
    this.body.append(el);
    this.body.scrollTop = this.body.scrollHeight;
    return el;
  }

  async ask(question, mode = 'ask') {
    const q = String(question || '').trim();
    if (!q || this.busy) return;
    this.open();
    this.busy = true;
    this.input.value = '';
    this.bubble('user', esc(q));
    const out = this.bubble('ai', `<p class="sm-ai-chat__thinking">${esc(this.cfg.thinking)}</p>`);
    let text = '';
    try {
      const r = await fetch(this.cfg.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, handle: this.cfg.handle, locale: this.cfg.locale, history: this.history, mode }),
      });
      if (!r.ok || !r.body) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || this.cfg.error);
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        text += dec.decode(value, { stream: true });
        out.innerHTML = md(text.replace(/\[\[product:[a-z0-9-]*\]?\]?/g, ''));
        this.body.scrollTop = this.body.scrollHeight;
      }
      const handles = [...new Set([...text.matchAll(/\[\[product:([a-z0-9-]+)\]\]/g)].map((m) => m[1]))].slice(0, 3);
      out.innerHTML = md(text.replace(/\[\[product:[a-z0-9-]+\]\]/g, '').trim());
      if (handles.length) await this.cards(handles, out);
      this.history.push({ role: 'user', content: q }, { role: 'assistant', content: text });
    } catch (e) {
      out.innerHTML = `<p>${esc(e.message || this.cfg.error)}</p>`;
    }
    this.busy = false;
    this.body.scrollTop = this.body.scrollHeight;
  }

  async cards(handles, after) {
    const wrap = document.createElement('div');
    wrap.className = 'sm-ai-chat__cards';
    for (const h of handles) {
      const p = await fetch(`${routes()}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (!p) continue;
      const v = p.variants.find((x) => x.available) || p.variants[0];
      const plans = (p.selling_plan_groups || []).flatMap((g) => g.selling_plans || []);
      const planPrices = (v.selling_plan_allocations || []).map((a) => ({ id: a.selling_plan_id, price: a.price, per: a.per_delivery_price }));
      const best = planPrices.sort((a, b) => a.price - b.price)[0];
      const plan120 = plans.find((s) => /120/.test(s.name)) || plans[0];
      const from = Math.min(v.price, ...(planPrices.map((x) => x.price)));
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=400` : '';
      const card = document.createElement('div');
      card.className = 'sm-ai-chat__card';
      card.innerHTML = `
        <a class="sm-ai-chat__card-img" href="${p.url}">${img ? `<img src="${img}" alt="${esc(p.title)}" width="200" height="200" loading="lazy">` : ''}</a>
        <b>${esc(p.title)}</b>
        <span class="sm-ai-chat__card-sub">${esc((p.description || '').replace(/<[^>]+>/g, ' ').split('. ')[0].trim().slice(0, 110))}</span>
        <span class="sm-ai-chat__card-price">${esc(this.cfg.from.replace('[price]', money(from, this.cfg.locale)))}</span>
        <span class="sm-ai-chat__card-btns">
          <button type="button" class="sm-btn sm-btn--primary sm-btn--s" data-buy>${esc(this.cfg.buy)}</button>
          <a class="sm-btn sm-btn--secondary sm-btn--s" href="${p.url}">${esc(this.cfg.more)}</a>
        </span>`;
      card.querySelector('[data-buy]').addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        btn.setAttribute('aria-busy', 'true');
        const item = { id: v.id, quantity: 1 };
        if (plan120 && best) item.selling_plan = plan120.id;
        const r = await fetch(`${routes()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
        btn.removeAttribute('aria-busy');
        if (r.ok) { this.dialog.close(); emit('sm:cart:updated', {}); emit('sm:cart:open', {}); }
      });
      wrap.append(card);
    }
    if (wrap.children.length) after.after(wrap);
  }
}

let chat;
document.querySelectorAll('[data-ai-widget]').forEach((widget) => {
  const cfg = JSON.parse(widget.querySelector('script[data-ai-config]')?.textContent || '{}');
  if (!cfg.endpoint) return;
  const input = widget.querySelector('[data-ask-ai-input]');
  const get = () => (chat ||= new SmAiChat(cfg));
  const send = () => { get().ask(input?.value); if (input) input.value = ''; };
  widget.querySelector('[data-ask-ai-submit]')?.addEventListener('click', send);
  input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } });
  widget.querySelectorAll('[data-ask-ai-chip]').forEach((chip) => chip.addEventListener('click', (e) => {
    e.stopImmediatePropagation();
    get().ask(chip.dataset.askAiChip);
  }, true));
  const tabs = widget.querySelectorAll('[data-ask-ai-tab]');
  tabs[1]?.addEventListener('click', () => get().ask(cfg.ingredients_question, 'ingredients'));
});

// the floating "Ask smööts AI" pill (sticky bar) opens the same chat
document.addEventListener('click', (e) => {
  const pill = e.target.closest('.sm-sticky-atc-bar__chat, [data-open-ai-chat]');
  if (!pill) return;
  const widget = document.querySelector('[data-ai-widget]');
  if (!widget) return;
  e.preventDefault();
  const cfg = JSON.parse(widget.querySelector('script[data-ai-config]')?.textContent || '{}');
  if (!cfg.endpoint) return;
  (chat ||= new SmAiChat(cfg)).open();
  chat.input.focus();
});
